/**
 * Every workflow this account has, from all four sources, as one list
 * (DOP-478).
 *
 * A workflow is a configured template plus its output plus its run history,
 * and the three kinds we have — KPI workflows, monitoring templates, loan tape
 * extractions — are three backends' worth of that same idea. The index needs
 * the list without having to know which API a row came from, so the adaptation
 * to one row shape happens here and nowhere else.
 *
 * Each source is read and reported INDEPENDENTLY: a monitoring outage must not
 * blank the KPI rows. `error` is therefore "something could not be read", set
 * alongside whatever did load, rather than a state that replaces the list.
 *
 * Nothing polls. Live run progress comes from `WorkflowRunNotifier`, which
 * already owns exactly one poll loop per run app-wide.
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { formatEpochMs } from "../components/Monitoring/monitoringFormat";
import { useWorkflowRunNotifier } from "./WorkflowRunNotifier";
import {
	deleteWorkflow,
	listWorkflows,
} from "../services/kpiWorkflowService";
import {
	type LoanTapeDashboardSummary,
	deleteLoanTapeDashboard,
	listLoanTapeDashboards,
} from "../services/loanTapeDashboardService";
import { dealScreeningService } from "../services/dealScreeningService";
import {
	type PortfolioSummary,
	listPortfolios,
	portfolioHref,
} from "../services/portfolioDataService";
import { monitoringService } from "../services/monitoringService";
import type { DealScreeningWorkflowListItem } from "../types/dealScreening";
import type { MonitoringTemplate } from "../types/monitoring";
import {
	INTERVAL_LABELS,
	type Workflow,
	type WorkflowRun,
	isTerminalRunStatus,
} from "../types/kpiWorkflow";
import {
	isDealScreeningEnabled,
	isMonitoringEnabled,
	isWorkflowsEnabled,
} from "../utils/featureFlags";

export type DashboardKind =
	| "kpi"
	| "monitoring"
	| "loan_tape"
	| "deal_screening"
	| "portfolio_data";

export interface DashboardStatusPill {
	label: string;
	/** How loudly the pill reads — a failure is not the same news as a cadence. */
	tone: "running" | "failed" | "neutral";
}

export interface DashboardIndexRow {
	/** Unique across kinds; two backends can mint the same public id. */
	key: string;
	kind: DashboardKind;
	/**
	 * The id this row has in its OWN backend, which `key` deliberately is not:
	 * `key` is namespaced because two backends can mint the same public id,
	 * and a delete has to send the bare id its API expects.
	 */
	sourceId: string;
	name: string;
	href: string;
	/** The pill beside the title. */
	typeLabel: string;
	/** The line under the title. Empty when the source carries nothing useful. */
	meta: string;
	pills: DashboardStatusPill[];
	/** Right column, first line. `null` when it has never run. */
	lastRun: string | null;
	/** Right column, second line — "Manual" when nothing is scheduled. */
	nextRun: string;
	/**
	 * When the workflow was created, epoch ms. Rows without a run are ordered
	 * by it. `null` only if a source ever sends something unreadable — every
	 * one of them carries a created time.
	 */
	createdAt: number | null;
	/** Live progress while a run is being watched, else `null`. */
	progress: { processed: number; total: number } | null;
	/**
	 * False for a workflow the page cannot delete: portfolio tables are built
	 * offline from the client's files, so the row offers no Delete.
	 */
	deletable?: boolean;
}

export const TYPE_LABELS: Record<DashboardKind, string> = {
	kpi: "KPIs",
	monitoring: "Monitoring",
	loan_tape: "Loan tape",
	deal_screening: "Opportunity memo",
	portfolio_data: "Portfolio data",
};

/** "quarterly" → "Quarterly". The contract sends the cadence lower-cased. */
const capitalise = (value: string): string =>
	value.charAt(0).toUpperCase() + value.slice(1);

const countLabel = (count: number, noun: string): string =>
	`${count} ${noun}${count === 1 ? "" : "s"}`;

/** A timestamp only if it is one — a partial fixture must not render "NaN". */
const epochOrNull = (value: number | null | undefined): number | null =>
	typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * What a workflow's own last run says about it.
 *
 * Only the two states worth interrupting the row for get a pill: a run in
 * flight, and one that failed. "Completed" is already told by the last-run
 * timestamp in the right column, and a pill repeating it would make every
 * healthy row shout.
 */
function kpiPills(run: WorkflowRun | null): DashboardStatusPill[] {
	if (!run) return [];
	if (!isTerminalRunStatus(run.status)) {
		return [{ label: run.status === "queued" ? "Queued" : "Running", tone: "running" }];
	}
	if (run.status === "failed") return [{ label: "Failed", tone: "failed" }];
	return [];
}

function kpiRow(workflow: Workflow): DashboardIndexRow {
	const meta = [
		countLabel(workflow.metric_keys.length, "metric"),
		INTERVAL_LABELS[workflow.interval],
		workflow.folder_scope === "all"
			? "All folders"
			: countLabel(workflow.folder_ids.length, "folder"),
	];
	const finished = workflow.last_run?.finished_at_ms ?? null;
	return {
		key: `kpi:${workflow.id}`,
		kind: "kpi",
		sourceId: workflow.id,
		name: workflow.name,
		href: `/workflows/kpi/${encodeURIComponent(workflow.id)}`,
		typeLabel: TYPE_LABELS.kpi,
		meta: meta.join(" · "),
		pills: kpiPills(workflow.last_run),
		lastRun: finished === null ? null : formatEpochMs(finished),
		// There is no scheduler: `auto_run` means "when a file lands", and
		// without it the only thing that starts a run is a person.
		nextRun: workflow.auto_run ? "On new files" : "Manual",
		createdAt: epochOrNull(workflow.created_at_ms),
		progress: null,
	};
}

function monitoringRow(template: MonitoringTemplate): DashboardIndexRow {
	const cadence = template.cadence ? capitalise(template.cadence) : null;
	// Fund, cadence and roster size are what this workflow IS — the same kind
	// of fact as the KPI row's "2 metrics · Monthly · All folders" — so they
	// belong on the meta line. As pills they competed with the type pill beside
	// the name and made a healthy monitoring row shout three times while a
	// healthy KPI row said nothing.
	const meta = [
		template.fund_name,
		cadence,
		typeof template.borrowers_count === "number" &&
		Number.isFinite(template.borrowers_count)
			? countLabel(template.borrowers_count, "borrower")
			: null,
	].filter((part): part is string => Boolean(part));
	return {
		key: `monitoring:${template.public_id}`,
		kind: "monitoring",
		sourceId: template.public_id,
		name: template.name,
		href: `/workflows/monitoring/${encodeURIComponent(template.public_id)}`,
		typeLabel: TYPE_LABELS.monitoring,
		meta: meta.join(" · "),
		// Pills are for STATUS. Nothing in the template payload carries one —
		// a run in flight or a covenant breach would, and neither is on this
		// contract yet (DOP-480).
		pills: [],
		lastRun: epochOrNull(template.updated_at) === null
			? null
			: formatEpochMs(template.updated_at),
		// The cadence IS the schedule the packages arrive on; there is no
		// computed next-run date to show yet.
		nextRun: cadence ?? "Manual",
		createdAt: epochOrNull(template.created_at),
		progress: null,
	};
}

/**
 * The same rule as `kpiPills`: only a run in flight or one that failed is
 * worth a pill. A completed run is already told by the last-run timestamp.
 */
function loanTapePills(
	status: LoanTapeDashboardSummary["status"],
): DashboardStatusPill[] {
	if (status === "pending") return [{ label: "Queued", tone: "running" }];
	if (status === "running") return [{ label: "Running", tone: "running" }];
	if (status === "failed") return [{ label: "Failed", tone: "failed" }];
	return [];
}

function loanTapeRow(summary: LoanTapeDashboardSummary): DashboardIndexRow {
	return {
		key: `loan_tape:${summary.id}`,
		kind: "loan_tape",
		sourceId: summary.id,
		name: summary.name,
		href: `/workflows/loan-tape/${encodeURIComponent(summary.id)}`,
		typeLabel: TYPE_LABELS.loan_tape,
		// The output workbook's name is only in the thread's blocks, and the
		// index will not spend a request per row to read it.
		meta: "",
		pills: loanTapePills(summary.status),
		lastRun:
			summary.last_run_ms === null ? null : formatEpochMs(summary.last_run_ms),
		nextRun: "Manual",
		createdAt: epochOrNull(summary.created_at_ms),
		progress: null,
	};
}

/**
 * A deal-screening workflow (DOP-497). One pill: deals never screened. What
 * a memo still needs from a person (blocking gaps, conflicts, gaps) lives on
 * the workflow's Needs review tab; repeating the count here said nothing the
 * tab does not say better.
 */
function dealScreeningRow(item: DealScreeningWorkflowListItem): DashboardIndexRow {
	const meta = [countLabel(item.deals_count, "deal"), "one memo per deal"];
	const pills: DashboardStatusPill[] = [];
	if (item.deals_not_run > 0) {
		pills.push({ label: `${countLabel(item.deals_not_run, "deal")} not run`, tone: "neutral" });
	}
	const lastRun = item.latest_run_at === null ? null : Date.parse(item.latest_run_at);
	return {
		key: `deal_screening:${item.public_id}`,
		kind: "deal_screening",
		sourceId: item.public_id,
		name: item.name,
		href: `/workflows/deal-screening/${encodeURIComponent(item.public_id)}`,
		typeLabel: TYPE_LABELS.deal_screening,
		meta: meta.join(" · "),
		pills,
		lastRun: epochOrNull(lastRun) === null ? null : formatEpochMs(lastRun),
		// A run starts when someone presses Run; nothing is scheduled.
		nextRun: "Manual",
		createdAt: epochOrNull(Date.parse(item.created_at)),
		progress: null,
	};
}

/** A deal's portfolio tables: built from the client's workbooks, read here. */
function portfolioRow(summary: PortfolioSummary): DashboardIndexRow {
	const built = summary.built_at === null ? null : Date.parse(summary.built_at);
	const meta = [
		countLabel(summary.house_count, "house"),
		countLabel(summary.plat_count, "plat"),
		summary.years.join(", "),
	].filter(Boolean);
	return {
		key: `portfolio_data:${summary.deal_name}`,
		kind: "portfolio_data",
		sourceId: summary.deal_name,
		name: `${summary.deal_name} portfolio`,
		href: portfolioHref(summary.deal_name),
		typeLabel: TYPE_LABELS.portfolio_data,
		meta: meta.join(" · "),
		pills:
			summary.review_count > 0
				? [{ label: `${summary.review_count} to check`, tone: "neutral" }]
				: [],
		lastRun: epochOrNull(built) === null ? null : formatEpochMs(built as number),
		// Rebuilt when the client sends new workbooks; nothing is scheduled.
		nextRun: "On new files",
		createdAt: epochOrNull(built),
		progress: null,
		deletable: false,
	};
}

export interface DashboardsIndex {
	rows: DashboardIndexRow[];
	loading: boolean;
	/** A source that could not be read. The rows that did load are still shown. */
	error: string | null;
	refresh: () => void;
	/**
	 * Delete one workflow, whichever backend it came from (DOP-491).
	 *
	 * Rejects with the backend's own message rather than swallowing it — the
	 * refusals that matter ("a run is still going") tell the user what to do
	 * next, and only the caller knows where to put that sentence.
	 */
	remove: (row: DashboardIndexRow) => Promise<void>;
}

/**
 * Held in a provider rather than called per consumer: the index page reads the
 * list and the "New workflow" modal refreshes it after a create, and two hook
 * instances would put two identical requests on the wire per visit.
 */
function useDashboardsIndexSource(): DashboardsIndex {
	const [workflows, setWorkflows] = useState<Workflow[]>([]);
	const [templates, setTemplates] = useState<MonitoringTemplate[]>([]);
	const [loanTapes, setLoanTapes] = useState<LoanTapeDashboardSummary[]>([]);
	const [screenings, setScreenings] = useState<DealScreeningWorkflowListItem[]>(
		[],
	);
	const [portfolios, setPortfolios] = useState<PortfolioSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// The app-wide watch: it holds the fresher run for anything in flight, and
	// it is the only thing polling.
	const {
		trackedWorkflowIds,
		runsByWorkflow,
		adoptWorkflows,
		settlements,
		forgetWorkflow,
	} = useWorkflowRunNotifier();

	const isMounted = useRef(true);
	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	/** Newest read wins; a Retry pressed over a slow first read must not lose. */
	const readId = useRef(0);

	const load = useCallback(async () => {
		const id = ++readId.current;
		setLoading(true);
		const failures: string[] = [];

		const [kpi, monitoring, tapes, screening, portfolio] = await Promise.all([
			listWorkflows().catch(() => {
				failures.push("your KPI workflows");
				return null;
			}),
			isMonitoringEnabled()
				? monitoringService.listTemplates().catch(() => {
						failures.push("your monitoring workflows");
						return null;
					})
				: Promise.resolve<MonitoringTemplate[]>([]),
			listLoanTapeDashboards().catch(() => {
				failures.push("your loan tape workflows");
				return null;
			}),
			isDealScreeningEnabled()
				? dealScreeningService.listWorkflows().catch(() => {
						failures.push("your deal screening workflows");
						return null;
					})
				: Promise.resolve<DealScreeningWorkflowListItem[]>([]),
			listPortfolios().catch(() => {
				failures.push("your portfolio data");
				return null;
			}),
		]);

		// Any successful workflow read is a hydration source for the notifier,
		// even a superseded one: a run it names is live whichever read saw it.
		if (kpi) adoptWorkflows(kpi);
		if (id !== readId.current || !isMounted.current) return;

		// A failed source keeps whatever it last showed rather than blanking —
		// a transient 502 does not un-create the user's workflows.
		if (kpi) setWorkflows(kpi);
		if (monitoring) setTemplates(monitoring);
		if (tapes) setLoanTapes(tapes);
		if (screening) setScreenings(screening);
		if (portfolio) setPortfolios(portfolio);
		setError(
			failures.length === 0 ? null : `Could not load ${failures.join(" or ")}.`,
		);
		setLoading(false);
	}, [adoptWorkflows]);

	/**
	 * Re-read the workflow list alone.
	 *
	 * Shares `readId` with the full `load` on purpose: both write `workflows`,
	 * so "newest read wins" has to be one rule across both, or a slow full load
	 * could land after a settlement refresh and repaint the pre-run row.
	 *
	 * Deliberately does NOT touch `loading` or `error`: nobody asked for this
	 * read, and turning the list into a spinner — or into an error line —
	 * because a background refresh failed would be news about the wrong thing.
	 */
	const refreshWorkflows = useCallback(async () => {
		const id = ++readId.current;
		const next = await listWorkflows();
		adoptWorkflows(next);
		if (id !== readId.current || !isMounted.current) return;
		setWorkflows(next);
	}, [adoptWorkflows]);

	/**
	 * A watched run reached its end somewhere above this provider.
	 *
	 * Until it settles, `rows` overlays the notifier's live run — but the
	 * notifier stops holding it the moment the run is no longer tracked, and the
	 * `Workflow` snapshot underneath is the one fetched BEFORE the run. Without
	 * this the row would drop straight back to the old last-run time with no
	 * failed pill, and stay wrong until something else refetched.
	 *
	 * Drained by cursor rather than reacting to "the latest" (the same rule as
	 * `useKpiWorkflows`): two runs can settle inside one React batch, and
	 * watching only the newest would skip the first.
	 */
	const settlementCursor = useRef(0);
	useEffect(() => {
		const fresh = settlements.filter(
			(settled) => settled.count > settlementCursor.current,
		);
		if (fresh.length === 0) return;
		settlementCursor.current = fresh[fresh.length - 1].count;
		// A background refresh: a failure leaves the last good list on screen
		// rather than throwing an unhandled rejection at a user who did nothing.
		void refreshWorkflows().catch(() => {});
	}, [settlements, refreshWorkflows]);

	// StrictMode's dev remount re-runs this with the SAME `load`; without the
	// guard every mount pays a duplicate fan-out.
	const lastLoadRef = useRef<typeof load | null>(null);
	useEffect(() => {
		if (lastLoadRef.current === load) return;
		lastLoadRef.current = load;
		// A tenant that opted out of workflows (DOP-483) has no page that could
		// show this list, so the three reads it costs on every visit are skipped.
		if (!isWorkflowsEnabled()) return;
		void load();
	}, [load]);

	const refresh = useCallback(() => {
		void load();
	}, [load]);

	/**
	 * Delete a workflow and drop its row.
	 *
	 * The row goes from local state rather than through a refetch: the delete
	 * already returned 204, and a re-read would put a whole fan-out on the wire
	 * to learn one thing this call just proved. `readId` is deliberately NOT
	 * bumped — this is not a read, and claiming the newest one would cancel a
	 * legitimate load that is still in flight.
	 *
	 * Nothing is removed optimistically. A row that vanishes and comes back
	 * because the backend refused is worse than a row that waits half a second.
	 */
	const remove = useCallback(
		async (row: DashboardIndexRow): Promise<void> => {
			switch (row.kind) {
				case "kpi":
					await deleteWorkflow(row.sourceId);
					// The app-wide watch may still be on this workflow: a run can
					// settle between the last poll and the delete, and the delete
					// only refuses runs it can SEE as active. Left alone the watch
					// would poll a 404 for as long as the tab stays open.
					forgetWorkflow(row.sourceId);
					if (isMounted.current) {
						setWorkflows((current) =>
							current.filter((workflow) => workflow.id !== row.sourceId),
						);
					}
					return;
				case "monitoring":
					await monitoringService.deleteTemplate(row.sourceId);
					if (isMounted.current) {
						setTemplates((current) =>
							current.filter(
								(template) => template.public_id !== row.sourceId,
							),
						);
					}
					return;
				case "loan_tape":
					// A loan tape workflow IS its chat, so deleting the chat is the
					// whole delete — there is no row of its own to remove.
					await deleteLoanTapeDashboard(row.sourceId);
					if (isMounted.current) {
						setLoanTapes((current) =>
							current.filter((summary) => summary.id !== row.sourceId),
						);
					}
					return;
				case "deal_screening":
					await dealScreeningService.deleteWorkflow(row.sourceId);
					if (isMounted.current) {
						setScreenings((current) =>
							current.filter((item) => item.public_id !== row.sourceId),
						);
					}
					return;
				case "portfolio_data":
					throw new Error(
						"Portfolio tables are built from the client's workbooks and can't be deleted here.",
					);
				default: {
					// A new kind of workflow must decide how it is deleted — what goes,
					// and what survives it — before it can ship. Without this, a kind
					// missing from the switch would fall through and resolve: the
					// dialog closes, the row stays, and nothing says why. The `never`
					// makes that a compile error, the same way `CONSEQUENCE` and
					// `ICONS` already refuse a kind they have no entry for.
					const unhandled: never = row.kind;
					throw new Error(`Deleting a ${String(unhandled)} workflow is not supported yet.`);
				}
			}
		},
		[forgetWorkflow],
	);

	const rows = useMemo(() => {
		// Grouped by kind rather than interleaved by time: a `Workflow` carries
		// `created_at_ms` and a `MonitoringTemplate` carries `updated_at`, which
		// are not the same clock, and sorting them together would shuffle the
		// list for reasons the reader cannot see.
		const kpiRows = workflows.map((workflow) => {
			const row = kpiRow(workflow);
			if (!trackedWorkflowIds.has(workflow.id)) return row;
			const watched = (runsByWorkflow[workflow.id] ?? []).find(
				(run) => !isTerminalRunStatus(run.status),
			);
			if (!watched) return row;
			return {
				...row,
				pills: kpiPills(watched),
				progress: {
					processed: watched.files_processed,
					total: watched.files_total,
				},
			};
		});
		return [
			...kpiRows,
			...templates.map(monitoringRow),
			...loanTapes.map(loanTapeRow),
			...screenings.map(dealScreeningRow),
			...portfolios.map(portfolioRow),
		];
	}, [
		workflows,
		templates,
		loanTapes,
		screenings,
		portfolios,
		trackedWorkflowIds,
		runsByWorkflow,
	]);

	return { rows, loading, error, refresh, remove };
}

const DashboardsIndexContext = createContext<DashboardsIndex | undefined>(
	undefined);

export const DashboardsIndexProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const value = useDashboardsIndexSource();
	return (
		<DashboardsIndexContext.Provider value={value}>
			{children}
		</DashboardsIndexContext.Provider>
	);
};

export function useDashboardsIndex(): DashboardsIndex {
	const context = useContext(DashboardsIndexContext);
	if (!context) {
		throw new Error(
			"useDashboardsIndex must be used inside a DashboardsIndexProvider.",
		);
	}
	return context;
}
